/**
 * 使用者管理頁面
 * 參照 UI: UI-1-4 員工帳號管理（User Management）
 * 功能：列表 + 搜尋/篩選 + 建立 + 編輯 + 重設密碼
 */
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminVenueListOptions } from '../../hooks/adminVenueQueries';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { apiClient } from '../../lib/api.ts';
import { useAuthStore } from '../../store/auth.store';
import type {
  UserDto,
  RoleDto,
  CreateUserDto,
  UpdateUserDto,
  AdminResetPasswordDto,
  VenueScope,
} from '@pokerroom/types';

/** API 回應包裝格式 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/** 場館清單項（供場館範圍指派） */
interface VenueOption {
  id: string;
  name: string;
  isActive: boolean;
}

/** 授予全館範圍所需的 ActionKey（與 @pokerroom/types ActionKey.USER_GRANT_VENUE_SCOPE 同值） */
const GRANT_VENUE_SCOPE_ACTION = 'user:grant_venue_scope';

/** 場館範圍表單欄位（Create/Update 共用） */
interface ScopeFields {
  venueScope?: VenueScope;
  venueIds?: string[];
  defaultVenueId?: string;
}

/** 兩個 ID 集合是否相同（忽略順序） */
function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

// ============================================================
// Sub-components
// ============================================================

export function RoleVenueHint({ venueName, isMember }: { venueName?: string; isMember: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <div className="text-xs text-base-content/70 mb-2" role="note">
      <p>{venueName ? t('roleCurrentVenue', { venue: venueName }) : t('roleVenueLoading')}</p>
      <p>{t('rolePerVenueHint')}</p>
      {venueName && !isMember && <p className="text-warning">{t('roleVenueMembershipRequired')}</p>}
    </div>
  );
}

/** 角色 Badge */
function RoleBadge({ name }: { name: string }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-primary/15 text-primary border border-primary/30 font-medium">
      {name}
    </span>
  );
}

/** 狀態指示點 */
function StatusDot({ active }: { active: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-base-content/30'}`}
      title={active ? t('statusEnabled') : t('statusDisabled')}
    />
  );
}

/** 場館範圍 Badge（全館 / 指定場館 · X +N） */
export function ScopeBadge({ user }: { user: UserDto }) {
  const { t } = useTranslation('admin');
  if (user.venueScope === 'ALL_VENUES') {
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-warning/15 text-warning border border-warning/40 font-medium">
        {t('scopeAllVenues')}
      </span>
    );
  }
  const names = user.venues.map((v) => v.venueName);
  const first = names[0];
  const extra = names.length - 1;
  return (
    <span
      title={names.join(', ')}
      className="inline-block px-2 py-0.5 rounded-full text-xs bg-base-content/10 text-base-content/70 border border-base-300 font-medium"
    >
      {first
        ? `${t('scopeSpecific')} · ${first}${extra > 0 ? ` +${extra}` : ''}`
        : t('scopeSpecific')}
    </span>
  );
}

/**
 * 場館範圍區塊（與角色權限分離）：單選全館/指定場館；指定場館時多選場館 + 指定預設館。
 * 無 USER_GRANT_VENUE_SCOPE 時「全館」停用；編輯自己時整區唯讀。
 */
export function VenueScopeSection({
  value,
  onChange,
  venues,
  canGrantAllVenues,
  readOnly = false,
}: {
  value: ScopeFields;
  onChange: (next: ScopeFields) => void;
  venues: VenueOption[];
  canGrantAllVenues: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('admin');
  const activeVenues = venues.filter((v) => v.isActive);
  const scope: VenueScope = value.venueScope ?? 'SPECIFIC';
  const selected = value.venueIds ?? [];

  if (readOnly) {
    return (
      <div className="rounded-lg border border-base-300 p-3">
        <label className="block text-sm font-medium text-base-content/70 mb-1">
          {t('venueScopeLabel')}
        </label>
        <p className="text-sm text-base-content/60">
          {scope === 'ALL_VENUES'
            ? t('scopeAllVenues')
            : `${t('scopeSpecific')}${selected.length ? ` · ${selected.length}` : ''}`}
        </p>
        <p className="text-xs text-base-content/40 mt-1">{t('selfScopeReadonly')}</p>
      </div>
    );
  }

  const toggleVenue = (id: string) => {
    const next = selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id];
    let def = value.defaultVenueId;
    if (!def || !next.includes(def)) def = next[0];
    onChange({ ...value, venueIds: next, defaultVenueId: def });
  };

  return (
    <div className="rounded-lg border border-base-300 p-3 space-y-2">
      <label className="block text-sm font-medium text-base-content/70">
        {t('venueScopeLabel')}
      </label>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="venueScope"
            checked={scope === 'SPECIFIC'}
            onChange={() => onChange({ ...value, venueScope: 'SPECIFIC' })}
            className="accent-primary"
          />
          <span className="text-sm text-base-content">{t('scopeSpecific')}</span>
        </label>
        <label
          className={`flex items-center gap-2 ${
            canGrantAllVenues ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          <input
            type="radio"
            name="venueScope"
            disabled={!canGrantAllVenues}
            checked={scope === 'ALL_VENUES'}
            onChange={() => onChange({ ...value, venueScope: 'ALL_VENUES' })}
            className="accent-primary"
          />
          <span className="text-sm text-base-content">{t('scopeAllVenues')}</span>
        </label>
      </div>
      {!canGrantAllVenues && (
        <p className="text-xs text-base-content/40">{t('noGrantAllVenues')}</p>
      )}
      {scope === 'ALL_VENUES' ? (
        <p className="text-xs text-base-content/50">{t('allVenuesHint')}</p>
      ) : (
        <div className="space-y-1">
          <p className="text-xs text-base-content/50">{t('selectVenues')}</p>
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {activeVenues.map((v) => {
              const isOn = selected.includes(v.id);
              return (
                <div
                  key={v.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-base-200 border border-base-300"
                >
                  <label className="flex items-center gap-2 cursor-pointer min-w-0 flex-1">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={() => toggleVenue(v.id)}
                      className="accent-primary"
                    />
                    <span className="text-sm text-base-content truncate">{v.name}</span>
                  </label>
                  {isOn && (
                    <button
                      type="button"
                      onClick={() => onChange({ ...value, defaultVenueId: v.id })}
                      className={`text-xs shrink-0 ${
                        value.defaultVenueId === v.id
                          ? 'text-primary font-semibold'
                          : 'text-base-content/40 hover:text-primary'
                      }`}
                    >
                      {value.defaultVenueId === v.id ? t('defaultVenueMark') : t('setDefault')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// 建立使用者 Modal
// ============================================================
interface CreateModalProps {
  roles: RoleDto[];
  venues: VenueOption[];
  canGrantAllVenues: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserDto) => void;
  isLoading: boolean;
}

function CreateUserModal({
  roles,
  venues,
  canGrantAllVenues,
  onClose,
  onSubmit,
  isLoading,
}: CreateModalProps) {
  const { venue: currentVenue } = useCurrentVenue();
  const dialogRef = useDialogFocus(true);
  const { t } = useTranslation('admin');
  const [form, setForm] = useState<CreateUserDto>({
    username: '',
    displayName: '',
    password: '',
    email: '',
    roleIds: [],
    mustChangePassword: true,
    venueScope: 'SPECIFIC',
    venueIds: [],
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const scope: VenueScope = form.venueScope ?? 'SPECIFIC';
  const canAssignCurrentRole = Boolean(
    currentVenue && (scope === 'ALL_VENUES' || form.venueIds?.includes(currentVenue.id)),
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.username || form.username.length < 4) e.username = t('usernameMinLength');
    if (!/^[a-zA-Z0-9_]+$/.test(form.username)) e.username = t('onlyAlphanumeric');
    if (!form.displayName) e.displayName = t('displayNameRequired');
    if (!form.password || form.password.length < 8) e.password = t('passwordMinLength');
    if (form.password !== confirmPassword) e.confirm = t('passwordMismatch');
    if (form.roleIds.length === 0) e.roles = t('roleRequired');
    if (!canAssignCurrentRole) e.roles = t('roleVenueMembershipRequired');
    if (scope === 'SPECIFIC' && (form.venueIds?.length ?? 0) === 0) e.venues = t('venueRequired');
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit({
      ...form,
      email: form.email || undefined,
      // ALL_VENUES ignores venueIds server-side; send them only for SPECIFIC.
      venueIds: scope === 'SPECIFIC' ? form.venueIds : undefined,
      defaultVenueId: scope === 'SPECIFIC' ? form.defaultVenueId : undefined,
    });
  };

  const toggleRole = (id: string) => {
    setForm((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(id)
        ? prev.roleIds.filter((r) => r !== id)
        : [...prev.roleIds, id],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('userManagement')}
        tabIndex={-1}
        className="bg-base-100 rounded-2xl border border-base-300 w-full max-w-md max-h-[calc(100dvh-32px)] overflow-y-auto p-4 sm:p-6 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-base-content mb-4">{t('createUser')}</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* 使用者名稱 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('usernameLabel')}
            </label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              placeholder={t('usernamePlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
            {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
          </div>

          {/* 顯示名稱 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('displayNameLabel')}
            </label>
            <input
              type="text"
              value={form.displayName}
              onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
              placeholder={t('displayNamePlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
            {errors.displayName && (
              <p className="text-red-500 text-xs mt-1">{errors.displayName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('emailOptional')}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="example@example.com"
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
          </div>

          {/* 密碼 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('initialPassword')}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              placeholder={t('passwordPlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          {/* 確認密碼 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('confirmPasswordLabel')}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('confirmPasswordPlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
            {errors.confirm && <p className="text-red-500 text-xs mt-1">{errors.confirm}</p>}
          </div>

          {/* 區塊一：場館範圍 */}
          <VenueScopeSection
            value={form}
            onChange={(next) => setForm((p) => ({ ...p, ...next }))}
            venues={venues}
            canGrantAllVenues={canGrantAllVenues}
          />
          {errors.venues && <p className="text-red-500 text-xs mt-1">{errors.venues}</p>}

          {/* 區塊二：角色權限 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('assignRole')}
            </label>
            <RoleVenueHint venueName={currentVenue?.name} isMember={canAssignCurrentRole} />
            <div className="grid grid-cols-2 gap-2">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-base-200 border border-base-300 cursor-pointer hover:border-primary transition"
                >
                  <input
                    type="checkbox"
                    checked={form.roleIds.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    disabled={!canAssignCurrentRole}
                    className="accent-primary"
                  />
                  <span className="text-sm text-base-content">{role.name}</span>
                </label>
              ))}
            </div>
            {errors.roles && <p className="text-red-500 text-xs mt-1">{errors.roles}</p>}
          </div>

          {/* 強制修改密碼 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.mustChangePassword}
              onChange={(e) => setForm((p) => ({ ...p, mustChangePassword: e.target.checked }))}
              className="accent-primary"
            />
            <span className="text-sm text-base-content/70">{t('forceChangePassword')}</span>
          </label>

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-11 px-4 py-2 rounded-lg bg-base-300 text-base-content/70 hover:bg-base-200 transition text-sm"
            >
              {t('cancelBtn')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 min-h-11 px-4 py-2 rounded-lg bg-primary text-primary-content hover:opacity-90 transition text-sm font-semibold disabled:opacity-50"
            >
              {isLoading ? t('creating') : t('createBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// 編輯使用者 Modal
// ============================================================
interface EditModalProps {
  user: UserDto;
  roles: RoleDto[];
  venues: VenueOption[];
  canGrantAllVenues: boolean;
  isSelf: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateUserDto) => void;
  isLoading: boolean;
  errorMessage?: string;
}

function EditUserModal({
  user,
  roles,
  venues,
  canGrantAllVenues,
  isSelf,
  onClose,
  onSubmit,
  isLoading,
  errorMessage,
}: EditModalProps) {
  const { venue: currentVenue } = useCurrentVenue();
  const dialogRef = useDialogFocus(true);
  const { t } = useTranslation('admin');
  const [emailError, setEmailError] = useState('');
  const [form, setForm] = useState<UpdateUserDto>({
    displayName: user.displayName,
    email: user.email ?? '',
    isActive: user.isActive,
    roleIds: [...user.roleIds],
    venueScope: user.venueScope,
    venueIds: user.venues.map((v) => v.venueId),
    defaultVenueId: user.venues.find((v) => v.isDefault)?.venueId,
  });
  const scope: VenueScope = form.venueScope ?? user.venueScope;
  const canAssignCurrentRole = Boolean(
    currentVenue && (scope === 'ALL_VENUES' || form.venueIds?.includes(currentVenue.id)),
  );

  const toggleRole = (id: string) => {
    setForm((prev) => ({
      ...prev,
      roleIds: prev.roleIds?.includes(id)
        ? prev.roleIds.filter((r) => r !== id)
        : [...(prev.roleIds ?? []), id],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentVenue) return;
    // Send only changed fields so a benign edit never triggers a needless
    // membership reconciliation / session revocation (authVersion bump).
    const payload: UpdateUserDto = {};
    if (form.displayName !== user.displayName) payload.displayName = form.displayName;
    const email = form.email?.trim() || '';
    const originalEmail = user.email?.trim() || '';
    if (email !== originalEmail) payload.email = email || null;
    if (form.isActive !== user.isActive) payload.isActive = form.isActive;
    if (canAssignCurrentRole && !sameIdSet(form.roleIds ?? [], user.roleIds))
      payload.roleIds = form.roleIds ?? [];

    if (!isSelf) {
      const scopeChanged = scope !== user.venueScope;
      if (scopeChanged) payload.venueScope = scope;
      if (scope === 'SPECIFIC') {
        const origIds = user.venues.map((v) => v.venueId);
        const origDefault = user.venues.find((v) => v.isDefault)?.venueId;
        const membershipChanged =
          !sameIdSet(form.venueIds ?? [], origIds) || form.defaultVenueId !== origDefault;
        if (scopeChanged || membershipChanged) {
          payload.venueIds = form.venueIds ?? [];
          payload.defaultVenueId = form.defaultVenueId;
        }
      }
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('userManagement')}
        tabIndex={-1}
        className="bg-base-100 rounded-2xl border border-base-300 w-full max-w-md max-h-[calc(100dvh-32px)] overflow-y-auto p-4 sm:p-6 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-base-content mb-1">{t('editUser')}</h2>
        <p className="text-sm text-base-content/50 mb-4">@{user.username}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* 顯示名稱 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('displayNameLabel2')}
            </label>
            <input
              type="text"
              value={form.displayName}
              onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('emailLabel')}
            </label>
            <input
              type="email"
              maxLength={128}
              aria-label={t('emailLabel')}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'edit-user-email-error' : undefined}
              onInvalid={() => setEmailError(t('invalidEmail'))}
              value={form.email ?? ''}
              onChange={(e) => {
                setEmailError('');
                setForm((p) => ({ ...p, email: e.target.value }));
              }}
              placeholder="example@example.com"
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
          </div>

          {emailError && (
            <p id="edit-user-email-error" role="alert" className="text-error text-sm">
              {emailError}
            </p>
          )}

          {/* 區塊一：場館範圍 */}
          {!user.isSystemUser && (
            <VenueScopeSection
              value={form}
              onChange={(next) => setForm((p) => ({ ...p, ...next }))}
              venues={venues}
              canGrantAllVenues={canGrantAllVenues}
              readOnly={isSelf}
            />
          )}

          {/* 區塊二：角色權限 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('roleLabel')}
            </label>
            <RoleVenueHint venueName={currentVenue?.name} isMember={canAssignCurrentRole} />
            <div className="grid grid-cols-2 gap-2">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-base-200 border border-base-300 cursor-pointer hover:border-primary transition"
                >
                  <input
                    type="checkbox"
                    checked={form.roleIds?.includes(role.id) ?? false}
                    onChange={() => toggleRole(role.id)}
                    disabled={!canAssignCurrentRole}
                    className="accent-primary"
                  />
                  <span className="text-sm text-base-content">{role.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 帳號狀態 */}
          {!user.isSystemUser && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                className="accent-primary"
              />
              <span className="text-sm text-base-content/70">{t('accountEnabled')}</span>
            </label>
          )}

          {errorMessage && (
            <p role="alert" className="text-error text-sm">
              {errorMessage}
            </p>
          )}

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-11 px-4 py-2 rounded-lg bg-base-300 text-base-content/70 hover:bg-base-200 transition text-sm"
            >
              {t('cancelBtn')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !currentVenue}
              className="flex-1 min-h-11 px-4 py-2 rounded-lg bg-primary text-primary-content hover:opacity-90 transition text-sm font-semibold disabled:opacity-50"
            >
              {isLoading ? t('saving') : t('saveBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// 重設密碼 Modal
// ============================================================
interface ResetPasswordModalProps {
  user: UserDto;
  onClose: () => void;
  onSubmit: (data: AdminResetPasswordDto) => void;
  isLoading: boolean;
}

function ResetPasswordModal({ user, onClose, onSubmit, isLoading }: ResetPasswordModalProps) {
  const dialogRef = useDialogFocus(true);
  const { t } = useTranslation('admin');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError(t('passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }
    onSubmit({ newPassword });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('resetPassword')}
        tabIndex={-1}
        className="bg-base-100 rounded-2xl border border-base-300 w-full max-w-sm max-h-[calc(100dvh-32px)] overflow-y-auto p-4 sm:p-6 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-base-content mb-1">{t('resetPassword')}</h2>
        <p className="text-sm text-base-content/50 mb-4">@{user.username}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('newPasswordLabel')}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('passwordMinLength')}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t('confirmPasswordLabel')}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('confirmPasswordLabel')}
              className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-base min-h-11"
            />
          </div>
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <p className="text-xs text-base-content/50">{t('forceNextLogin')}</p>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-11 px-4 py-2 rounded-lg bg-base-300 text-base-content/70 hover:bg-base-200 transition text-sm"
            >
              {t('cancelBtn')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 min-h-11 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition text-sm font-semibold disabled:opacity-50"
            >
              {isLoading ? t('resetting') : t('resetPasswordBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Main Page
// ============================================================

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; user: UserDto }
  | { type: 'reset'; user: UserDto };

function AdminUsersPageContent({ revision }: { revision: number }) {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [editError, setEditError] = useState('');
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (msgTimer.current) clearTimeout(msgTimer.current);
    },
    [],
  );

  /** 顯示暫時訊息 */
  const showMessage = (msg: string, isError = false) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    if (isError) {
      setErrorMessage(msg);
      msgTimer.current = setTimeout(() => setErrorMessage(''), 4000);
    } else {
      setSuccessMessage(msg);
      msgTimer.current = setTimeout(() => setSuccessMessage(''), 4000);
    }
  };

  const { data: usersData, isLoading: usersLoading } = useQuery(
    adminVenueListOptions<UserDto>('users', revision),
  );
  const { data: rolesData } = useQuery(adminVenueListOptions<RoleDto>('roles', revision));
  const { data: venuesData } = useQuery(adminVenueListOptions<VenueOption>('venues', revision));

  const authUser = useAuthStore((s) => s.user);
  const allowedActions = useAuthStore((s) => s.allowedActions);
  const canGrantAllVenues = (allowedActions ?? []).includes(GRANT_VENUE_SCOPE_ACTION);

  const users = usersData ?? [];
  const roles = rolesData ?? [];
  const venues = venuesData ?? [];

  // 建立使用者
  const createMutation = useMutation({
    mutationFn: (data: CreateUserDto) => apiClient.post<ApiResponse<UserDto>>('/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModal({ type: 'none' });
      showMessage(t('userCreated'));
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('createFailed');
      showMessage(msg, true);
    },
  });

  // 編輯使用者
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) =>
      apiClient.patch<ApiResponse<UserDto>>(`/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModal({ type: 'none' });
      showMessage(t('userUpdated'));
    },
    onError: (err: unknown) => {
      const data = (
        err as {
          response?: { data?: { message?: string | string[]; errors?: { validation?: string[] } } };
        }
      )?.response?.data;
      const message = data?.errors?.validation ?? data?.message;
      setEditError(
        Array.isArray(message)
          ? message.join(' · ')
          : typeof message === 'string'
            ? message
            : t('updateFailed'),
      );
    },
  });

  // 重設密碼
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdminResetPasswordDto }) =>
      apiClient.post<ApiResponse<void>>(`/users/${id}/reset-password`, data),
    onSuccess: () => {
      setModal({ type: 'none' });
      showMessage(t('passwordReset'));
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('resetFailed');
      showMessage(msg, true);
    },
  });

  // roleId → roleName 映射
  const roleMap = Object.fromEntries(roles.map((r) => [r.id, r.name]));

  // 篩選邏輯
  const filteredUsers = users.filter((u) => {
    const matchSearch =
      !search ||
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      u.displayName.toLowerCase().includes(search.toLowerCase());

    const matchRole = filterRole === 'all' || u.roleIds.includes(filterRole);

    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && u.isActive) ||
      (filterStatus === 'inactive' && !u.isActive);

    return matchSearch && matchRole && matchStatus;
  });

  return (
    <div className="[&_button]:min-h-11 [&_button]:text-sm min-h-screen bg-base-100 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-base-content/40 hover:text-base-content transition text-sm"
            >
              {t('dashboard')}
            </Link>
            <span className="text-base-content/30">/</span>
            <h1 className="heading-font text-xl font-bold text-base-content">
              {t('userManagement')}
            </h1>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="min-h-11 px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-semibold hover:opacity-90 transition"
          >
            + {t('createUser')}
          </button>
        </div>

        {/* Toast messages */}
        {successMessage && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Search + Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="flex-1 min-w-48 px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content text-base min-h-11 focus:outline-hidden focus:border-primary"
          />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content text-base min-h-11 focus:outline-hidden focus:border-primary"
          >
            <option value="all">{t('allRoles')}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content text-base min-h-11 focus:outline-hidden focus:border-primary"
          >
            <option value="all">{t('allStatus')}</option>
            <option value="active">{t('statusActive')}</option>
            <option value="inactive">{t('statusInactive')}</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-base-200 rounded-2xl border border-base-300 overflow-hidden">
          {usersLoading ? (
            <div className="p-12 text-center text-base-content/40 text-sm">{t('loading')}</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-12 text-center text-base-content/40 text-sm">
              {search || filterRole !== 'all' || filterStatus !== 'all'
                ? t('noFilteredUsers')
                : t('noUsers')}
            </div>
          ) : (
            <>
              <div className="sm:hidden divide-y divide-base-300">
                {filteredUsers.map((user) => (
                  <article key={user.id} className="p-4 space-y-3 min-w-0">
                    <div className="space-y-1">
                      <h2 className="text-lg font-semibold break-words [overflow-wrap:anywhere]">
                        {user.displayName}
                      </h2>
                      <p className="text-sm font-mono [overflow-wrap:anywhere]">
                        {user.username} {user.isSystemUser && t('systemUser')}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {user.roleIds.map((id) =>
                        roleMap[id] ? <RoleBadge key={id} name={roleMap[id]} /> : null,
                      )}
                      {user.roleIds.length === 0 && <span>{t('rolesPending')}</span>}
                      <ScopeBadge user={user} />
                      <span>{user.isActive ? t('statusEnabled') : t('statusDisabled')}</span>
                      <span>{user.totpEnabled ? t('twoFaEnabled') : t('twoFaDisabled')}</span>
                    </div>
                    <div className="flex flex-wrap items-start gap-2">
                      <button
                        onClick={() => {
                          setEditError('');
                          setModal({ type: 'edit', user });
                        }}
                        className="min-h-11 px-3 rounded-lg bg-base-300 text-sm hover:text-primary"
                      >
                        {t('editBtn')}
                      </button>
                      {!user.isSystemUser && (
                        <details>
                          <summary className="min-h-11 flex items-center px-3 rounded-lg bg-base-300 text-sm cursor-pointer">
                            {t('more', { ns: 'common', defaultValue: '更多' })}
                          </summary>
                          <button
                            onClick={() => setModal({ type: 'reset', user })}
                            className="min-h-11 mt-2 px-3 rounded-lg bg-base-300 text-sm hover:text-red-400"
                          >
                            {t('resetPasswordBtn')}
                          </button>
                        </details>
                      )}
                    </div>
                  </article>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-base-300">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnAccount')}
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnDisplayName')}
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnRole')}
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnVenueScope')}
                      </th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnTwofa')}
                      </th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnStatus')}
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
                        {t('columnActions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-base-300/50 last:border-0 hover:bg-base-300/30 transition"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {/* Avatar */}
                            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                              {user.username[0]?.toUpperCase()}
                            </div>
                            <span className="text-sm text-base-content font-mono">
                              {user.username}
                            </span>
                            {user.isSystemUser && (
                              <span className="text-xs text-base-content/40">
                                {t('systemUser')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-base-content">{user.displayName}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {user.roleIds.map((id) =>
                              roleMap[id] ? <RoleBadge key={id} name={roleMap[id]} /> : null,
                            )}
                            {user.roleIds.length === 0 && (
                              <span className="text-xs text-base-content/60">
                                {t('rolesPending')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <ScopeBadge user={user} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          {user.totpEnabled ? (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-emerald-500"
                              title={t('twoFaEnabled')}
                            />
                          ) : (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-base-content/20"
                              title={t('twoFaDisabled')}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StatusDot active={user.isActive} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditError('');
                                setModal({ type: 'edit', user });
                              }}
                              className="px-3 py-1 rounded-lg bg-base-300 text-base-content/70 hover:bg-primary/15 hover:text-primary transition text-xs"
                            >
                              {t('editBtn')}
                            </button>
                            {!user.isSystemUser && (
                              <button
                                onClick={() => setModal({ type: 'reset', user })}
                                className="px-3 py-1 rounded-lg bg-base-300 text-base-content/70 hover:bg-red-500/15 hover:text-red-400 transition text-xs"
                              >
                                {t('resetPasswordBtn')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* 計數 */}
        {!usersLoading && (
          <p className="text-xs text-base-content/40 mt-2">
            {t('showCount', { shown: filteredUsers.length, total: users.length })}
          </p>
        )}
      </div>

      {/* Modals */}
      {modal.type === 'create' && (
        <CreateUserModal
          roles={roles}
          venues={venues}
          canGrantAllVenues={canGrantAllVenues}
          onClose={() => setModal({ type: 'none' })}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}
      {modal.type === 'edit' && (
        <EditUserModal
          user={modal.user}
          roles={roles}
          venues={venues}
          canGrantAllVenues={canGrantAllVenues}
          isSelf={authUser?.id === modal.user.id}
          onClose={() => setModal({ type: 'none' })}
          onSubmit={(data) => {
            setEditError('');
            updateMutation.mutate({ id: modal.user.id, data });
          }}
          errorMessage={editError}
          isLoading={updateMutation.isPending}
        />
      )}
      {modal.type === 'reset' && (
        <ResetPasswordModal
          user={modal.user}
          onClose={() => setModal({ type: 'none' })}
          onSubmit={(data) => resetPasswordMutation.mutate({ id: modal.user.id, data })}
          isLoading={resetPasswordMutation.isPending}
        />
      )}
    </div>
  );
}

function AdminUsersPage() {
  const revision = useAuthStore((state) => state.sessionRevision);
  return <AdminUsersPageContent key={revision} revision={revision} />;
}

export default AdminUsersPage;
